import React from 'react';
import { Sparkles, BookOpen, GraduationCap, Wand2 } from 'lucide-react';
import { cn } from '../lib/utils';
import { Badge } from './ui/badge';

/**
 * Mapeo desde nivel de enseñanza al perfil de rúbrica inferido por defecto.
 * El usuario puede sobreescribir manualmente con el selector.
 */
const NIVEL_A_PERFIL_INFERIDO = {
    'Diferencial': 'Generalista',
    'Educación Parvularia': 'Generalista',
    'Educación Básica': 'Básica',
    'Educación Media': 'Media',
};

export function inferirPerfilRubrica(nivel) {
    return NIVEL_A_PERFIL_INFERIDO[nivel] || null;
}

const PERFILES = [
    {
        id: 'Generalista',
        titulo: 'Generalista',
        subtitulo: 'Parvularia, Diferencial o aula integral',
        descripcion: 'Mirada integral y transversal. Articulas múltiples áreas del desarrollo: lenguaje, pensamiento, vínculo, socioemocional y juego.',
        cuandoElegir: 'Ideal si trabajas en Parvularia, Diferencial o si tu clase grabada no se agota en una sola disciplina.',
        icon: Sparkles,
        tono: {
            ring: 'ring-emerald-500/60',
            border: 'border-emerald-400',
            bg: 'bg-emerald-50/70',
            iconBg: 'bg-emerald-100',
            iconColor: 'text-emerald-700',
            badge: 'bg-emerald-100 text-emerald-800',
            hover: 'hover:border-emerald-300',
        },
    },
    {
        id: 'Básica',
        titulo: 'Educación Básica',
        subtitulo: '1° a 6° Básico',
        descripcion: 'Mirada de acompañamiento, modelamiento y progresión. La clase muestra andamios visibles, retroalimentación inmediata y avance gradual.',
        cuandoElegir: 'Ideal si modelas el pensamiento en voz alta, sostienes rutinas de aula y avanzas de lo simple a lo complejo dentro de una misma clase.',
        icon: BookOpen,
        tono: {
            ring: 'ring-sky-500/60',
            border: 'border-sky-400',
            bg: 'bg-sky-50/70',
            iconBg: 'bg-sky-100',
            iconColor: 'text-sky-700',
            badge: 'bg-sky-100 text-sky-800',
            hover: 'hover:border-sky-300',
        },
    },
    {
        id: 'Media',
        titulo: 'Educación Media',
        subtitulo: '7° Básico a 4° Medio',
        descripcion: 'Mirada de profundidad disciplinar, análisis y argumentación. Pides evidencia, contraste de fuentes y pensamiento crítico.',
        cuandoElegir: 'Ideal si tu clase exige argumentar con evidencia, contrastar posiciones y promover autonomía intelectual creciente.',
        icon: GraduationCap,
        tono: {
            ring: 'ring-violet-500/60',
            border: 'border-violet-400',
            bg: 'bg-violet-50/70',
            iconBg: 'bg-violet-100',
            iconColor: 'text-violet-700',
            badge: 'bg-violet-100 text-violet-800',
            hover: 'hover:border-violet-300',
        },
    },
];

export default function PerfilRubricaSelector({ value, onChange, nivel }) {
    const inferido = inferirPerfilRubrica(nivel);
    const seleccionado = value || inferido;

    return (
        <div className="space-y-4" data-testid="perfil-rubrica-selector">
            <div className="flex items-start gap-3 rounded-xl border border-amber-200 bg-amber-50/60 p-4">
                <Wand2 className="mt-0.5 h-5 w-5 shrink-0 text-amber-600" />
                <div className="space-y-1">
                    <p className="text-sm font-semibold text-amber-900">
                        ¿Con qué perfil de rúbrica te identificas?
                    </p>
                    <p className="text-xs text-amber-800 leading-relaxed">
                        Las rúbricas oficiales del <strong>Módulo 2 (Clase Grabada)</strong> son las mismas para todos/as, pero la <strong>mirada pedagógica</strong> cambia.
                        Elige el perfil que mejor refleja cómo enseñas; la IA ajustará su lenguaje, ejemplos, análisis y recomendaciones.
                        Útil para casos frontera: 7°–8° Básico, generalistas en transición, perfiles híbridos.
                    </p>
                </div>
            </div>

            <div className="grid gap-3 sm:grid-cols-3" role="radiogroup" aria-label="Selecciona tu perfil de rúbrica">
                {PERFILES.map((p) => {
                    const Icon = p.icon;
                    const activo = seleccionado === p.id;
                    const esInferidoNoElegido = !value && inferido === p.id;
                    return (
                        <button
                            type="button"
                            key={p.id}
                            role="radio"
                            aria-checked={activo}
                            onClick={() => onChange(p.id)}
                            className={cn(
                                'group relative flex h-full flex-col items-start gap-3 rounded-2xl border-2 bg-white p-5 text-left transition-all duration-200',
                                'focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2',
                                activo
                                    ? cn(p.tono.border, p.tono.bg, 'shadow-md ring-2', p.tono.ring)
                                    : cn('border-slate-200', p.tono.hover, 'hover:shadow-sm hover:-translate-y-0.5')
                            )}
                            data-testid={`perfil-rubrica-card-${p.id}`}
                        >
                            <div className="flex w-full items-start justify-between">
                                <div className={cn('flex h-11 w-11 items-center justify-center rounded-xl transition-colors', p.tono.iconBg)}>
                                    <Icon className={cn('h-5 w-5', p.tono.iconColor)} />
                                </div>
                                <div className="flex flex-col items-end gap-1">
                                    {activo && (
                                        <Badge className={cn('text-[10px] font-semibold', p.tono.badge)} data-testid={`perfil-rubrica-activo-${p.id}`}>
                                            Seleccionado
                                        </Badge>
                                    )}
                                    {!activo && esInferidoNoElegido && (
                                        <Badge variant="outline" className="text-[10px] text-slate-500 border-slate-300">
                                            Sugerido por tu nivel
                                        </Badge>
                                    )}
                                </div>
                            </div>

                            <div className="space-y-1">
                                <h3 className="font-heading font-bold text-slate-900 text-base">{p.titulo}</h3>
                                <p className="text-xs font-medium text-slate-500">{p.subtitulo}</p>
                            </div>

                            <p className="text-sm text-slate-700 leading-relaxed">
                                {p.descripcion}
                            </p>

                            <p className="mt-auto pt-2 text-xs text-slate-500 leading-relaxed border-t border-slate-100/80 w-full">
                                <span className="font-semibold text-slate-600">¿Cuándo elegirlo?</span> {p.cuandoElegir}
                            </p>
                        </button>
                    );
                })}
            </div>

            {seleccionado && (
                <p className="text-xs text-slate-500 leading-relaxed" data-testid="perfil-rubrica-feedback">
                    La IA usará la mirada <strong className="text-slate-700">{seleccionado}</strong> al conversar contigo sobre indicadores del Módulo 2.
                    Puedes cambiar este perfil cuando quieras; la IA se ajustará inmediatamente en tu próxima interacción.
                </p>
            )}
        </div>
    );
}
